import React from "react";
import SectionHeader, { SectionWrapper, Reveal } from "./SectionHeader";
import { motion } from "framer-motion";

const SATELLITES = [
  { label: "India-First", note: "Rooted in Bharat", angle: 0, color: "#00F0FF", tid: "audience-india" },
  { label: "Tier 1 + Tier 2", note: "Metros & heartland", angle: 90, color: "#FF00FF", tid: "audience-tiers" },
  { label: "Highly Engaged", note: "Comment · Share · Save", angle: 180, color: "#FFDE00", tid: "audience-engaged" },
  { label: "Digital Native", note: "Mobile-first behaviour", angle: 270, color: "#00F0FF", tid: "audience-digital" },
];

const AudienceProfile = () => {
  return (
    <SectionWrapper id="audience">
      <SectionHeader
        code="// CH.05 — AUDIENCE"
        title="Audience.Node"
        kicker="A cohort that moves markets and shapes mainstream culture before the mainstream even sees it."
        tid="audience"
      />

      <div className="relative flex flex-col lg:flex-row gap-10 items-center">
        {/* Radar diagram */}
        <div className="relative w-full max-w-[480px] mx-auto lg:mx-0 lg:ml-8 aspect-square flex-none">
          {/* rings — sized as % of container so they scale */}
          <div className="radar-ring" style={{ width: "100%", height: "100%" }} />
          <div className="radar-ring" style={{ width: "72%", height: "72%" }} />
          <div className="radar-ring" style={{ width: "46%", height: "46%" }} />

          {/* grid lines */}
          <div className="absolute inset-0 grid-bg-fine opacity-30" />

          {/* pulse */}
          <div className="radar-pulse" />

          {/* center */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="absolute top-1/2 left-1/2 w-[40%] max-w-[200px] aspect-square rounded-full glass-card flex flex-col items-center justify-center text-center p-4"
            style={{
              borderColor: "rgba(0, 240, 255, 0.55)",
              boxShadow: "0 0 60px rgba(0, 240, 255, 0.35), inset 0 0 30px rgba(0, 240, 255, 0.15)",
              x: "-50%",
              y: "-50%",
            }}
            data-testid="audience-center"
          >
            <span className="micro-label text-cyan-400 mb-1">CORE COHORT</span>
            <span className="font-display font-black text-3xl md:text-4xl text-white glow-cyan">
              16–34
            </span>
            <span className="font-display font-bold uppercase text-xs md:text-sm text-white/75 tracking-widest mt-1">
              Gen Bharat
            </span>
          </motion.div>

          {/* satellites — positional wrapper + inner motion for animation */}
          {SATELLITES.map((s, i) => {
            const rad = (s.angle * Math.PI) / 180;
            // Percent offset of satellite center from radar center (of parent)
            const R_PCT = 36;
            const leftPct = 50 + Math.cos(rad) * R_PCT;
            const topPct = 50 + Math.sin(rad) * R_PCT;
            return (
              <div
                key={s.label}
                className="absolute pointer-events-none"
                style={{
                  left: `${leftPct}%`,
                  top: `${topPct}%`,
                  transform: "translate(-50%, -50%)",
                }}
                data-testid={s.tid}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.6 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.1, duration: 0.6 }}
                  className="glass-card hud-corners px-2.5 py-2 md:px-4 md:py-3 w-[108px] md:w-[150px] text-center pointer-events-auto"
                  style={{ borderColor: `${s.color}55` }}
                >
                  <span className="hud-tr" />
                  <span className="hud-bl" />
                  <span
                    className="block font-display font-bold uppercase text-[10px] md:text-sm tracking-tight leading-tight"
                    style={{ color: s.color, textShadow: `0 0 14px ${s.color}55` }}
                  >
                    {s.label}
                  </span>
                  <span className="block micro-label text-white/45 mt-1 text-[8px] md:text-[11px] leading-tight">{s.note}</span>
                </motion.div>
              </div>
            );
          })}
        </div>

        {/* Right copy */}
        <div className="flex-1 space-y-6">
          <Reveal>
            <div className="glass-card hud-corners p-8">
              <span className="hud-tr" />
              <span className="hud-bl" />
              <div className="micro-label text-cyan-400 mb-3">INSIGHT · 01</div>
              <p className="font-display font-bold text-xl md:text-2xl leading-snug tracking-tight">
                The 16–34 India cohort is the <span className="text-cyan-400">single most influential</span> commercial &amp; cultural bloc in the country.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="glass-card hud-corners p-8">
              <span className="hud-tr" />
              <span className="hud-bl" />
              <div className="micro-label text-fuchsia-400 mb-3">INSIGHT · 02</div>
              <p className="text-white/65 leading-relaxed text-sm md:text-base">
                We speak directly to their frequency — from metros to Tier-2
                heartland — with content that respects context, intelligence
                and intent. The result: trust that converts into influence,
                commerce and movement.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </SectionWrapper>
  );
};

export default AudienceProfile;
